// ============================================================
// TARGET PATH IN YOUR REPO:
//   app/src/app/components/skyplus/wf-runtime/services/alfresco-repo-browse.service.ts
// ============================================================
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthenticationService, AppConfigService } from '@alfresco/adf-core';
import { firstValueFrom } from 'rxjs';

/**
 * Lets the document-type studio browse the Alfresco repository (Company Home,
 * Sites, and any folder tree) so the registry person picks a base folder
 * visually instead of typing a path. Bearer + ecmHost, same as the indexing
 * service.
 *
 * <p>Each returned folder carries a {@code repoPath} already normalised to the
 * form {@code resolveOrCreatePath()} expects — i.e. the "/Company Home" prefix
 * stripped — so a plain folder yields {@code /ZIMASADMS/APPEALS} and a site
 * folder yields {@code /Sites/<site>/documentLibrary/<folder>}.
 */
@Injectable({ providedIn: 'root' })
export class AlfrescoRepoBrowseService {

  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);
  private readonly appConfig = inject(AppConfigService);

  private ecmHost = '';

  constructor() {
    this.ecmHost = (this.appConfig.get<string>('ecmHost', '') || '').replace(/\/+$/, '');
  }

  private headers(): HttpHeaders {
    const token = this.auth.getToken();
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }

  /** List child FOLDERS of a node (default Company Home), newest folder model first. */
  async listFolders(parentId: string = '-root-'): Promise<RepoFolder[]> {
    const url = `${this.ecmHost}/alfresco/api/-default-/public/alfresco/versions/1/nodes/${parentId}/children`;
    const params = new HttpParams()
      .set('where', '(isFolder=true)')
      .set('include', 'path')
      .set('maxItems', '500')
      .set('orderBy', 'name ASC');
    const resp: any = await firstValueFrom(
      this.http.get(url, { headers: this.headers(), params })
    );
    const entries: any[] = resp?.list?.entries ?? [];
    return entries.map(e => {
      const entry = e.entry;
      const pathName: string = entry?.path?.name ?? '';
      const full = pathName ? `${pathName}/${entry.name}` : `/${entry.name}`;
      return {
        id: entry.id,
        name: entry.name,
        repoPath: this.stripCompanyHome(full)
      } as RepoFolder;
    });
  }

  /**
   * Resolve a repository path (e.g. {@code /Sites/eacc/documentLibrary}) to its
   * node id + name so a browser can START there. Returns null for a blank/root
   * path (caller falls back to Company Home) or if the path doesn't resolve.
   */
  async resolvePath(repoPath: string): Promise<{ id: string; name: string } | null> {
    const rel = (repoPath || '').replace(/^\/+/, '').replace(/\/+$/, '');
    if (!rel) { return null; }
    const url = `${this.ecmHost}/alfresco/api/-default-/public/alfresco/versions/1/nodes/-root-`;
    const params = new HttpParams().set('relativePath', rel);
    try {
      const resp: any = await firstValueFrom(this.http.get(url, { headers: this.headers(), params }));
      const entry = resp?.entry;
      return entry?.id ? { id: entry.id, name: entry.name } : null;
    } catch {
      return null;
    }
  }

  /** List child NODES (folders AND files) of a node — for attaching documents to a case. */
  async listChildren(parentId: string = '-root-'): Promise<RepoNode[]> {
    const url = `${this.ecmHost}/alfresco/api/-default-/public/alfresco/versions/1/nodes/${parentId}/children`;
    const params = new HttpParams()
      .set('include', 'path')
      .set('maxItems', '500')
      .set('orderBy', 'name ASC');
    const resp: any = await firstValueFrom(this.http.get(url, { headers: this.headers(), params }));
    const entries: any[] = resp?.list?.entries ?? [];
    const nodes: RepoNode[] = entries.map(e => {
      const entry = e.entry;
      const pathName: string = entry?.path?.name ?? '';
      const full = pathName ? `${pathName}/${entry.name}` : `/${entry.name}`;
      return {
        id: entry.id,
        name: entry.name,
        isFolder: !!entry.isFolder,
        isFile: !!entry.isFile,
        repoPath: this.stripCompanyHome(full)
      } as RepoNode;
    });
    // Folders first, then files, each alphabetical.
    return nodes.sort((a, b) =>
      (a.isFolder === b.isFolder) ? a.name.localeCompare(b.name) : (a.isFolder ? -1 : 1));
  }

  /** Turn '/Company Home/Sites/eacc/documentLibrary/Appeals' into '/Sites/eacc/documentLibrary/Appeals'. */
  private stripCompanyHome(full: string): string {
    const stripped = full.replace(/^\/Company Home/, '');
    return stripped.length ? stripped : '/';
  }
}

export interface RepoFolder {
  id: string;
  name: string;
  repoPath: string;
}

export interface RepoNode {
  id: string;
  name: string;
  isFolder: boolean;
  isFile: boolean;
  repoPath: string;
}